/* eslint-disable no-useless-catch */
import { ENV } from "../utils";

export class PeriodGrade {
  baseApi = ENV.BASE_API;

  /**
   * Registers a single grade.
   * @param {string} accessToken - JWT token
   * @param {Object} data - { courseId, academicPeriodId, competenceId, literalGrade, observations }
   * @returns {Promise<Object>} - Created grade
   */
  async registerGrade(accessToken, data) {
    try {
      const url = `${this.baseApi}/${ENV.API_ROUTES.PERIOD_GRADE}`;
      const params = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(data),
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 201) throw result;
      return result.data || result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Registers multiple grades in bulk.
   * @param {string} accessToken - JWT token
   * @param {Array} grades - Array of grade objects
   * @returns {Promise<Object>} - Result with registered count
   */
  async registerBulkGrades(accessToken, grades) {
    try {
      const url = `${this.baseApi}/${ENV.API_ROUTES.PERIOD_GRADE}/bulk`;
      const params = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ grades }),
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 201) throw result;
      return result.data || result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Fetches grades of a student in a specific period.
   * @param {string} accessToken - JWT token
   * @param {number} studentId - Student ID
   * @param {number} academicPeriodId - Period ID
   * @returns {Promise<Object>} - Student grades with summary
   */
  async getGradesByStudentAndPeriod(accessToken, studentId, academicPeriodId) {
    try {
      const url = `${this.baseApi}/${ENV.API_ROUTES.PERIOD_GRADE}/student/${studentId}/period/${academicPeriodId}`;
      const params = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw result;
      return result.data || result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Fetches grades by course with optional period filter.
   * @param {string} accessToken - JWT token
   * @param {number} courseId - Course ID
   * @param {number} academicPeriodId - Optional period ID
   * @returns {Promise<Object>} - Course grades by period
   */
  async getGradesByCourse(accessToken, courseId, academicPeriodId = null) {
    try {
      let url = `${this.baseApi}/${ENV.API_ROUTES.PERIOD_GRADE}/course/${courseId}`;
      if (academicPeriodId) {
        url += `?academicPeriodId=${academicPeriodId}`;
      }
      const params = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw result;
      return result.data || result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Fetches annual report of a student.
   * @param {string} accessToken - JWT token
   * @param {number} studentId - Student ID
   * @param {number} academicCycleId - Academic cycle ID
   * @returns {Promise<Object>} - Annual report with all subjects and grades
   */
  async getAnnualReport(accessToken, studentId, academicCycleId) {
    try {
      const url = `${this.baseApi}/${ENV.API_ROUTES.PERIOD_GRADE}/student/${studentId}/annual/${academicCycleId}`;
      const params = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw result;
      return result.data || result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Fetches a grade by ID.
   * @param {string} accessToken - JWT token
   * @param {number} gradeId - Grade ID
   * @returns {Promise<Object>} - Grade object
   */
  async getGrade(accessToken, gradeId) {
    try {
      const url = `${this.baseApi}/${ENV.API_ROUTES.PERIOD_GRADE}/${gradeId}`;
      const params = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw result;
      return result.data || result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Deletes a grade (soft delete).
   * @param {string} accessToken - JWT token
   * @param {number} gradeId - Grade ID
   * @returns {Promise<Object>} - Deletion result
   */
  async deleteGrade(accessToken, gradeId) {
    try {
      const url = `${this.baseApi}/${ENV.API_ROUTES.PERIOD_GRADE}/${gradeId}`;
      const params = {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw result;
      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Fetches all data for grade registration table: students, competences, and existing grades.
   * @param {string} accessToken - JWT token
   * @param {number} academicCycleId - Academic cycle ID
   * @param {number} gradeId - Grade ID
   * @param {number} subjectId - Subject ID
   * @param {number} periodId - Academic period ID
   * @returns {Promise<Object>} - { students, competences, grades, summary }
   */
  async getGradesByFilters(
    accessToken,
    academicCycleId,
    gradeId,
    subjectId,
    periodId,
  ) {
    try {
      const url = `${this.baseApi}/${ENV.API_ROUTES.PERIOD_GRADE}/by-filters/academic-cycle/${academicCycleId}/grade/${gradeId}/subject/${subjectId}/period/${periodId}`;
      const params = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw result;
      return result;
    } catch (error) {
      throw error;
    }
  }
}
